import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useCopyToClipboard,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, relativeTime } from '../lib/format';
import type { SessionUser } from '../App';

interface DevToken {
  id: string;
  name: string;
  user: string;
  scopes: string[];
  toolset: { key: string; name: string } | null;
  allowProductionInvoke: boolean;
  last4: string;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export function DeveloperTokensPage({ user }: { user: SessionUser }) {
  const tokens = useResource<{ items: DevToken[] }>('/api/admin/developer-tokens');
  const toolsets = useResource<{ items: Array<{ id: string; key: string; name: string }> }>(
    '/api/admin/mcp-toolsets',
  );
  const toast = useToast();
  const [copied, copy] = useCopyToClipboard();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    toolsetId: '',
    expiresInDays: '30',
    allowProductionInvoke: false,
  });
  const [issued, setIssued] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<DevToken | null>(null);

  return (
    <>
      <PageHeader
        title="Tokeny deweloperskie MCP"
        description="Osobiste, zawsze wygasające poświadczenia dla Codex i Claude Code. Oddzielne od kluczy produkcyjnych i domyślnie bez dostępu do produkcji."
        actions={
          <Button variant="primary" onClick={() => setOpen(true)}>
            Utwórz token
          </Button>
        }
      />

      <Callout>
        Token deweloperski nigdy nie może odczytać poświadczeń dostawcy — taki zakres po prostu nie
        istnieje. W dziennikach i kosztach jest rozliczany osobno, nigdy jako aplikacja produkcyjna.
      </Callout>

      {tokens.loading ? (
        <Skeleton rows={4} />
      ) : (
        <Card tight>
          <DataTable
            rows={tokens.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak tokenów"
                description="Utwórz token, aby podłączyć Codex lub Claude Code."
              />
            }
            columns={[
              {
                key: 'token',
                header: 'Token',
                render: (row) => (
                  <>
                    <div>{row.name}</div>
                    <div
                      className="ah-mono"
                      style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}
                    >
                      …{row.last4}
                    </div>
                  </>
                ),
              },
              { key: 'user', header: 'Właściciel', render: (row) => row.user },
              {
                key: 'toolset',
                header: 'Zestaw narzędzi',
                render: (row) =>
                  row.toolset ? (
                    <span className="ah-mono">{row.toolset.key}</span>
                  ) : (
                    <Badge tone="warn">cały katalog</Badge>
                  ),
              },
              {
                key: 'scopes',
                header: 'Zakresy',
                render: (row) => (
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {row.scopes.map((scope) => (
                      <Badge
                        key={scope}
                        tone={scope === 'production:invoke' ? 'danger' : 'neutral'}
                      >
                        {scope}
                      </Badge>
                    ))}
                  </div>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => {
                  const expired = new Date(row.expiresAt).getTime() <= Date.now();
                  if (row.revokedAt) return <Badge tone="danger">unieważniony</Badge>;
                  if (expired) return <Badge tone="danger">wygasł</Badge>;
                  return <Badge tone="ok">aktywny</Badge>;
                },
              },
              { key: 'expires', header: 'Wygasa', render: (row) => formatDateTime(row.expiresAt) },
              {
                key: 'used',
                header: 'Ostatnio',
                render: (row) => (row.lastUsedAt ? relativeTime(row.lastUsedAt) : 'nigdy'),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  !row.revokedAt ? (
                    <Button size="sm" variant="danger" onClick={() => setRevoking(row)}>
                      Unieważnij
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowy token deweloperski"
        description="Token jest osobisty. Ustaw go lokalnie w API_HUB_DEV_TOKEN i nigdy nie commituj."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.name}
              onClick={async () => {
                try {
                  const result = await api.post<{ plaintextToken: string }>(
                    '/api/admin/developer-tokens',
                    {
                      name: form.name,
                      ...(form.toolsetId ? { toolsetId: form.toolsetId } : {}),
                      ...(form.expiresInDays ? { expiresInDays: Number(form.expiresInDays) } : {}),
                      allowProductionInvoke: form.allowProductionInvoke,
                    },
                  );
                  setIssued(result.plaintextToken);
                  setOpen(false);
                  setForm({
                    name: '',
                    toolsetId: '',
                    expiresInDays: '30',
                    allowProductionInvoke: false,
                  });
                  tokens.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field label="Nazwa" hint="Np. „laptop — projekt sklep”.">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field
          label="Zestaw narzędzi MCP"
          hint="Zalecane: ogranicza token do operacji jednego projektu."
        >
          {(id) => (
            <Select
              id={id}
              value={form.toolsetId}
              onChange={(event) => setForm({ ...form, toolsetId: event.target.value })}
            >
              <option value="">— bez ograniczenia (cały katalog, tylko sandbox) —</option>
              {(toolsets.data?.items ?? []).map((toolset) => (
                <option key={toolset.id} value={toolset.id}>
                  {toolset.key} — {toolset.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Ważność (dni)" hint="Token zawsze wygasa. Maksymalnie 365 dni.">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={form.expiresInDays}
              onChange={(event) => setForm({ ...form, expiresInDays: event.target.value })}
            />
          )}
        </Field>
        {user.role === 'OWNER' && (
          <Checkbox
            label="Zezwól na wywołania produkcyjne"
            hint="Domyślnie wyłączone. Wymaga roli OWNER i jest w pełni audytowane."
            checked={form.allowProductionInvoke}
            onChange={(checked) => setForm({ ...form, allowProductionInvoke: checked })}
          />
        )}
      </Modal>

      <Modal
        open={issued !== null}
        onOpenChange={(open2) => !open2 && setIssued(null)}
        title="Token utworzony"
        footer={
          <Button variant="primary" onClick={() => setIssued(null)}>
            Skopiowałem, zamknij
          </Button>
        }
      >
        <Callout tone="danger">
          Wartość pokazywana tylko raz. Ustaw ją lokalnie i nie commituj do repozytorium.
        </Callout>
        <pre className="ah-code">{issued}</pre>
        <Button onClick={() => issued && copy(issued)}>
          {copied ? 'Skopiowano' : 'Kopiuj do schowka'}
        </Button>
        <pre
          className="ah-code"
          style={{ marginTop: 12 }}
        >{`export API_HUB_DEV_TOKEN="${'<'}wklej token${'>'}"`}</pre>
      </Modal>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open2) => !open2 && setRevoking(null)}
        title="Unieważnić token?"
        description={revoking ? `Token „${revoking.name}” przestanie działać natychmiast.` : ''}
        confirmLabel="Unieważnij"
        destructive
        onConfirm={async () => {
          if (!revoking) return;
          try {
            await api.post(`/api/admin/developer-tokens/${revoking.id}/revoke`, {
              reason: 'Unieważniony z panelu',
            });
            toast.success('Token unieważniony');
            tokens.reload();
          } catch (error) {
            const described = describeError(error);
            toast.error(described.title, described.body);
          }
        }}
      />
    </>
  );
}
